import { useCallback, useEffect, useRef, useState } from 'react';
import { type Messages, useCorti } from 'providers/CortiProvider';
import { type AudioLevelController, startAudioLevel } from 'utils/audioLevel';
import useCheckMicAccess from 'hooks/useCheckMicAccess';
import { useAudioDevice } from 'providers/AudioDeviceProvider';
import { useRecordingFlag } from 'hooks/useRecordingFlag';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export type TranscriptMessage = { text: string; isFinal: boolean };

interface UseDictationOptions {
  /** Also called while the mode is not known yet. */
  onNoDevice?: () => void;
}

export const useDictation = (
  onTranscript: (data: TranscriptMessage) => void,
  key: string,
  { onNoDevice }: UseDictationOptions = {}
) => {
  const { connect, sendAudio, sendEnd, handleMessage, close } = useCorti();
  const audioDevice = useAudioDevice();
  const selectedDeviceId = audioDevice.selectedDevice?.deviceId;
  const canDictate = audioDevice.canRecord;
  const markRecording = useRecordingFlag();
  const [activeDictation, setActiveDictation] = useState<string | undefined>(undefined);
  const [audioLevel, setAudioLevel] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioLevelCtlRef = useRef<AudioLevelController | null>(null);
  const { micPermissionDenied, dismiss } = useCheckMicAccess();
  const { t } = useTranslation();

  const stop = useCallback(async () => {
    if (mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.stop();
      } catch (error) {
        console.error(error);
      } finally {
        mediaRecorderRef.current = null;
      }
    }

    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((t) => t.stop());
      } catch (error) {
        console.error(error);
      } finally {
        streamRef.current = null;
      }
    }

    if (audioLevelCtlRef.current) {
      try {
        await audioLevelCtlRef.current?.stop();
      } catch (error) {
        console.error(error);
      } finally {
        audioLevelCtlRef.current = null;
        setAudioLevel(0);
      }
    }

    try {
      close();
    } catch (error) {
      console.error('error while closing the websocket', error);
    }
    setActiveDictation(undefined);
    markRecording(false);
  }, [close, markRecording]);

  const initiateStop = useCallback(async () => {
    if (mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.requestData();
        mediaRecorderRef.current.stop();
      } catch (error) {
        console.error('error while stopping the media recorder', error);
        await stop();
      }
    } else {
      await stop();
    }
  }, [stop, mediaRecorderRef]);

  const stopCorti = useCallback(() => {
    try {
      sendEnd();
    } catch (error) {
      console.error(error);
    }
  }, [sendEnd]);
  const start = useCallback(async () => {
    try {
      await stop();
      if (micPermissionDenied) {
        toast.error(t('mic_access_denied'));
        return;
      }
      if (!canDictate) {
        onNoDevice?.();
        return;
      }
      await connect();

      handleMessage((message: Messages) => {
        if (message?.type === 'transcript' && message?.data) {
          const data = message.data as TranscriptMessage;
          onTranscript({ text: data.text, isFinal: Boolean(data.isFinal) });
          return;
        }
        if (message?.type === 'ended') {
          void stop();
        }
        if (message?.type === 'error') {
          toast.error(t('errors.errorWhileTranscribing'));
          console.error('Corti error:', message.error);
        }
      });

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : true,
      });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      streamRef.current = stream;
      markRecording(true);

      try {
        audioLevelCtlRef.current = startAudioLevel(stream, setAudioLevel);
      } catch (meterErr) {
        console.warn('Audio level meter unavailable', meterErr);
      }

      mediaRecorder.ondataavailable = (event) => {
        if (!event.data || typeof event.data === 'undefined') {
          console.warn('No audio data available');
          return;
        }
        if (event.data.size > 0) {
          sendAudio(event.data);
        }
      };

      mediaRecorder.onerror = (ev) => {
        console.error('MediaRecorder error', ev);
      };

      mediaRecorder.onstop = () => {
        void stopCorti();
      };

      mediaRecorder.start(1000);
      setActiveDictation(key);
    } catch (e) {
      toast.error(t('errors.errorWhileTranscribing'));
      console.error('Failed to start dictation', e);
      await stop();
    }
  }, [
    stop,
    micPermissionDenied,
    canDictate,
    onNoDevice,
    selectedDeviceId,
    markRecording,
    connect,
    handleMessage,
    t,
    onTranscript,
    sendAudio,
    stopCorti,
    key,
  ]);

  useEffect(() => {
    return () => {
      void stop();
    };
  }, [close, stop]);

  return {
    activeDictation,
    audioLevel,
    start,
    stop,
    initiateStop,
    dismiss,
  } as const;
};
